import { type ReactElement, type ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Inbox,
  FileText,
  ShoppingBag,
  Users,
  Wrench,
  Sparkles,
  Star,
  Heart,
  User as UserIcon,
  LogOut,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { cn } from '@/lib/utils'

interface NavItem {
  to: string
  label: string
  icon: ReactNode
}

const CLIENT_NAV: NavItem[] = [
  { to: '/client', label: 'Tableau de bord', icon: <LayoutDashboard size={18} /> },
  { to: '/client/catalog', label: 'Services & Produits', icon: <Sparkles size={18} /> },
  { to: '/client/requests/new', label: 'Nouvelle demande', icon: <Heart size={18} /> },
  { to: '/client/requests', label: 'Mes demandes', icon: <Inbox size={18} /> },
  { to: '/client/orders', label: 'Mes commandes', icon: <ShoppingBag size={18} /> },
  { to: '/client/profile', label: 'Mon profil', icon: <UserIcon size={18} /> },
]

const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Tableau de bord', icon: <LayoutDashboard size={18} /> },
  { to: '/admin/requests', label: 'Demandes', icon: <Inbox size={18} /> },
  { to: '/admin/quotes', label: 'Devis', icon: <FileText size={18} /> },
  { to: '/admin/orders', label: 'Commandes', icon: <ShoppingBag size={18} /> },
  { to: '/admin/clients', label: 'Clients', icon: <Users size={18} /> },
  { to: '/admin/services', label: 'Services', icon: <Wrench size={18} /> },
  { to: '/admin/products', label: 'Produits', icon: <Sparkles size={18} /> },
  { to: '/admin/reviews', label: 'Avis', icon: <Star size={18} /> },
]

export function AppLayout({ children }: { children: ReactNode }): ReactElement {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  if (!user) return <></>

  const nav = user.role === 'ADMIN' ? ADMIN_NAV : CLIENT_NAV

  return (
    <div className="grid min-h-screen grid-cols-[260px_1fr] bg-muted/30">
      <aside className="flex flex-col border-r border-border/70 bg-primary text-primary-foreground">
        <div className="border-b border-primary-foreground/10 px-6 py-6">
          <Link to="/" className="font-display text-2xl tracking-tight">
            MyPivot
          </Link>
          <p className="mt-1 text-xs uppercase tracking-widest text-accent">
            {user.role === 'ADMIN' ? 'Back-office' : 'Espace client'}
          </p>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to.endsWith('/client') || item.to.endsWith('/admin')}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-accent/15 text-accent'
                    : 'text-primary-foreground/70 hover:bg-primary-foreground/5 hover:text-primary-foreground',
                )
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-primary-foreground/10 p-4">
          <div className="mb-3 px-2">
            <div className="text-sm font-medium">{user.name}</div>
            <div className="truncate text-xs text-primary-foreground/60">{user.email}</div>
          </div>
          <button
            onClick={() => {
              logout()
              navigate('/login')
            }}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-primary-foreground/80 hover:bg-primary-foreground/5"
          >
            <LogOut size={16} /> Se déconnecter
          </button>
        </div>
      </aside>

      <main className="overflow-auto">
        <div className="mx-auto max-w-7xl px-8 py-10">{children}</div>
      </main>
    </div>
  )
}
